import { Card, CardTitle } from '../ui/card';
import { tailwindBreakpointSizesToImageSizes } from '~/utils/images/tailwind-breakpoints-to-image-sizes';
import { FecomImage } from '../images/fecom-image';
import { Price } from './price';
import { ProductListQuickView } from './product-list-quick-view';
import { Roundels } from './roundels';
import { type components } from '~/fecom-api/schema';
import { ProductListAddToBasket } from './product-list-add-to-basket';
import StockStatus from './stock-status';
import { cn } from '~/lib/utils';
import { ProductInlineLink } from './product-inline-link';
import { ProductDetailsStarRating } from './product-details-star-rating';
import { ProductsILoveButton } from './products-i-love-button';
import { type GtmItemListId } from '~/utils/gtm/gtm-helpers';
import { InlineLink } from '../common/inline-link';

type ProductListItemProps = {
  prioritiseImage?: boolean;
  fecomMediaBaseUrl: string;
  product: components['schemas']['Product'];
  className?: string;
  gtmListId?: GtmItemListId;
  gtmListName?: string;
  buttonVariant?: 'default' | 'outline';
};

export function ProductListItem({
  prioritiseImage: prioritseImage,
  product,
  fecomMediaBaseUrl,
  className,
  gtmListId,
  gtmListName,
  buttonVariant,
}: ProductListItemProps) {
  const productUrl = product.urlPath;

  return (
    <Card
      className={cn(
        'product-list-item',
        'row-span-4 flex grid-cols-1 flex-col gap-1.5 rounded-3xl bg-white/50 p-2 transition-shadow duration-200 hover:shadow-lg',
        className,
      )}
    >
      <div className="relative row-start-1 aspect-square">
        <div className="relative">
          <ProductInlineLink
            sliLogUrl={product.sliLogUrl}
            href={productUrl ?? '#'}
            className="block"
            product={product}
            gtmListId={gtmListId}
            gtmListName={gtmListName}
          >
            <div className="relative aspect-square overflow-hidden rounded-lg bg-white">
              <FecomImage
                // we need the key here to force the image to reload when the product changes
                key={product.imageId}
                src={product.imageId ?? ''}
                fill
                priority={prioritseImage ?? false}
                className="object-contain"
                fecomMediaBaseUrl={fecomMediaBaseUrl}
                alt={product.productName ?? ''}
                sizes={tailwindBreakpointSizesToImageSizes(
                  {
                    md: '33vw',
                    lg: '25vw',
                    xl: '20vw',
                    '2xl': '17vw',
                  },
                  '50vw',
                )}
              />
            </div>
          </ProductInlineLink>
          <ProductsILoveButton
            catRef={product.catalogueRef}
            className="absolute right-1 top-1 h-6 w-6"
            variant="main"
            isInitialFavourite={product.isFavourite}
          />
        </div>
        <Roundels
          roundels={product.roundels}
          fecomMediaBaseUrl={fecomMediaBaseUrl}
          isDiscountPrice={product.isDiscountPrice}
          isContractPrice={product.isContractPrice}
        />
        <div className="absolute bottom-0 left-0 right-0 flex rounded-b-lg transition duration-200">
          <ProductListQuickView
            fecomMediaBaseUrl={fecomMediaBaseUrl}
            initalProduct={product}
            title="Quick View"
          />
        </div>
      </div>

      <div className="min-h-[18px] grow">
        <div className="flex">
          <p className="text-sm font-semibold leading-[1.125rem]">
            {product.brandName || <>&nbsp;</>}
          </p>
          <ProductDetailsStarRating
            className="ml-auto"
            variant="list-item"
            maxRating={5}
            ratingCount={
              product.reviews?.productReviewSummary.numberOfReviews.total
            }
            rating={product.reviews?.productReviewSummary.starsAverage}
          />
        </div>
        <CardTitle className="font-normal">
          <ProductInlineLink
            sliLogUrl={product.sliLogUrl}
            href={productUrl ?? '#'}
            variant="inherit"
            tabIndex={-1}
            className="block text-base leading-6 no-underline hover:underline focus:underline"
            product={product}
            gtmListId={gtmListId}
            gtmListName={gtmListName}
          >
            {product.productName}
          </ProductInlineLink>
        </CardTitle>
      </div>
      <div>
        <p
          className="text-sm leading-tight text-[#626262]"
          data-testid="product-code"
        >
          {product.catalogueRef}
        </p>
        <StockStatus
          classname="mt-1"
          variant="small"
          stockInfo={product.stockInfo}
          isDirectDelivery={product.isDirectDelivery}
        />
      </div>
      <div className="mt-auto flex flex-col gap-2">
        <div>
          <div className="mt-2">
            {product.isContractPrice && (
              <div className="text-sm font-normal leading-tight text-muted-foreground">
                Your contract price
              </div>
            )}
            <div>
              {!!product.wasPriceIncVat && !!product.wasPriceExcVat && (
                <p className="text-xs text-[#626262] line-through">
                  <Price
                    priceIncVat={product.wasPriceIncVat}
                    priceExcVat={product.wasPriceExcVat}
                    showVatStatusText
                    className="block"
                    vatStatusClassName="text-xs"
                  />
                </p>
              )}
              <p className="text-lg font-semibold">
                <Price
                  priceIncVat={product.priceIncVat}
                  priceExcVat={product.priceExcVat}
                  showVatStatusText
                  className={cn('block', {
                    'text-success':
                      !!product.wasPriceIncVat && !!product.wasPriceExcVat,
                  })}
                  vatStatusClassName="text-[#626262]"
                  isPriceBreakPrice={product.isPriceBreakPrice}
                />
              </p>
              <p className="font-semibold">
                <Price
                  priceIncVat={product.priceIncVat}
                  priceExcVat={product.priceExcVat}
                  showVatStatusText
                  showOppositeVatStatus
                  className="block text-sm"
                  vatStatusClassName="text-[#626262] text-xs"
                />
              </p>
            </div>
          </div>
        </div>
        <div>
          <div>
            {product.isVariant ? (
              <p className="pb-2 text-sm font-normal leading-normal text-muted-foreground">
                <InlineLink
                  href={product.urlPath}
                  variant="inherit"
                  className="no-underline hover:underline"
                  tabIndex={-1}
                >
                  + More Options
                </InlineLink>
              </p>
            ) : (
              <>&nbsp;</>
            )}
          </div>
          <ProductListAddToBasket
            product={product}
            variant="standard"
            buttonVariant={buttonVariant}
          />
        </div>
      </div>
    </Card>
  );
}
